import { useEffect, useState } from "react";
import axios from "axios";
import { Bell, BellOff, Volume2, VolumeX } from "lucide-react";
import { API } from "../App";
import { Card, CardContent } from "./ui/card";
import { useAuth } from "../context/AuthContext";

const PREFS_LOCAL_KEY = "ndaku_goal_prefs";

/**
 * GoalAlertSettings — Iter 109
 *
 * Three toggles wired to `GET / PATCH /api/me/preferences`:
 *   - Goal alerts (master switch)
 *   - Goal sound (synthesised crowd-cheer in `<GoalCelebration>`)
 *   - Goal push notifications (Service Worker → web-push)
 *
 * Mirrors the chosen state to localStorage so non-API consumers
 * (`<GoalCelebration>` + `<LivePushNotifier>`) can read it without
 * waiting for an authenticated round-trip on every render.
 */
const TOGGLES = [
  {
    key: "goal_alerts_enabled",
    label: "Goal alerts",
    desc: "Master switch — when off, neither sound nor push fires.",
    Icon: Bell,
    OffIcon: BellOff,
  },
  {
    key: "goal_sound_enabled",
    label: "Goal celebration sound",
    desc: "Plays a short cheer + chord burst when a goal is detected.",
    Icon: Volume2,
    OffIcon: VolumeX,
  },
  {
    key: "goal_push_enabled",
    label: "Goal push notifications",
    desc: "Browser notification (works even when the tab is closed).",
    Icon: Bell,
    OffIcon: BellOff,
  },
];

const DEFAULT_PREFS = {
  goal_alerts_enabled: true,
  goal_sound_enabled: true,
  goal_push_enabled: true,
};

export const writeGoalPrefsLocal = (prefs) => {
  try { localStorage.setItem(PREFS_LOCAL_KEY, JSON.stringify(prefs)); } catch {}
};
export const readGoalPrefsLocal = () => {
  try {
    const v = localStorage.getItem(PREFS_LOCAL_KEY);
    return v ? { ...DEFAULT_PREFS, ...JSON.parse(v) } : { ...DEFAULT_PREFS };
  } catch {
    return { ...DEFAULT_PREFS };
  }
};

export const GoalAlertSettings = () => {
  const { token } = useAuth() || {};
  const [prefs, setPrefs] = useState(() => readGoalPrefsLocal());
  const [savingKey, setSavingKey] = useState(null);

  // Initial sync from server
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/me/preferences`)
      .then((r) => {
        if (cancelled) return;
        const next = { ...DEFAULT_PREFS, ...(r.data || {}) };
        setPrefs(next);
        writeGoalPrefsLocal(next);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [token]);

  const setOne = async (key, value) => {
    const next = { ...prefs, [key]: value };
    // If the master switch is being turned off, broadcast that so the
    // local listeners can react without waiting for the per-toggle
    // states (which the user is allowed to leave at their preferred
    // values for when they re-enable the master).
    setPrefs(next);
    writeGoalPrefsLocal(next);
    setSavingKey(key);
    try {
      await axios.patch(`${API}/me/preferences`, { [key]: value });
    } catch {
      // Soft-fail — keep the local state; next page-load will resync.
    } finally {
      setSavingKey(null);
    }
  };

  const masterOff = !prefs.goal_alerts_enabled;

  return (
    <Card className="border-border" data-testid="goal-alert-settings">
      <CardContent className="p-5 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-muted-foreground inline-flex items-center gap-1.5 mb-1">
            <Bell className="w-3 h-3" />
            Goal alerts
          </div>
          <h3 className="font-display text-xl font-bold tracking-tight">Goal alert settings</h3>
          <p className="text-xs text-muted-foreground">
            Choose how you want to be notified when a goal is scored in a match
            you're watching.
          </p>
        </div>

        <div className="space-y-2.5">
          {TOGGLES.map((tg) => {
            const enabled = !!prefs[tg.key];
            const isMaster = tg.key === "goal_alerts_enabled";
            const dimmed = !isMaster && masterOff;
            const Icon = enabled ? tg.Icon : tg.OffIcon;
            return (
              <label
                key={tg.key}
                data-testid={`goal-pref-${tg.key}`}
                className={`flex items-center gap-3 p-3 rounded-xl border border-border bg-card cursor-pointer transition-colors ${
                  dimmed ? "opacity-40 cursor-not-allowed" : "hover:bg-muted"
                }`}
              >
                <div className={`p-2 rounded-lg ${enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{tg.label}</div>
                  <div className="text-[11px] text-muted-foreground leading-relaxed">{tg.desc}</div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  disabled={dimmed || savingKey === tg.key}
                  onClick={() => setOne(tg.key, !enabled)}
                  data-testid={`goal-pref-toggle-${tg.key}`}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                    enabled ? "bg-primary" : "bg-muted"
                  } ${dimmed ? "cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`inline-block w-5 h-5 rounded-full bg-background shadow transform transition-transform ${
                      enabled ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default GoalAlertSettings;
